import { Button, confirm, Input, Modal, toast } from "@devhop/ui";
import { useState } from "react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { RowActions } from "@/shared/ui/RowActions";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  useCreateVehicleModelCode,
  useDeleteVehicleModelCode,
  useVehicleModelCodesQuery,
} from "../api/queries";

type VehicleModelCodesSectionProps = {
  modelId: string;
};

export function VehicleModelCodesSection({
  modelId,
}: VehicleModelCodesSectionProps) {
  const canCreate = useActionPermission(["catalog:create"]);
  const canDelete = useActionPermission(["catalog:delete"]);

  const codesQuery = useVehicleModelCodesQuery(modelId);
  const createCode = useCreateVehicleModelCode(modelId);
  const deleteCode = useDeleteVehicleModelCode(modelId);

  const [codeOpen, setCodeOpen] = useState(false);
  const [nfCodeType, setNfCodeType] = useState<"spec" | "option">("spec");
  const [nfCode, setNfCode] = useState("");
  const [nfCodeLabel, setNfCodeLabel] = useState("");
  const [nfCodeNotes, setNfCodeNotes] = useState("");

  const modelCodes = codesQuery.data ?? [];

  const resetCodeForm = () => {
    setNfCodeType("spec");
    setNfCode("");
    setNfCodeLabel("");
    setNfCodeNotes("");
  };

  return (
    <>
      <section className="space-y-3 rounded-xl border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold text-base">Spec / option codes</h3>
            <p className="text-muted-foreground text-xs">
              ຄລັງລະຫັດຂອງຮຸ່ນ — ເລືອກຜູກກັບແຕ່ລະ configuration ໃນໜ້າ EPC
            </p>
          </div>
          {canCreate && (
            <Button type="button" size="sm" onClick={() => setCodeOpen(true)}>
              ເພີ່ມ code
            </Button>
          )}
        </div>
        {codesQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className="py-2 pr-3">ປະເພດ</th>
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Label</th>
                  <th className="py-2 pr-3">ໝາຍເຫດ</th>
                  {canDelete && <th className="py-2" />}
                </tr>
              </thead>
              <tbody>
                {modelCodes.map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 capitalize">{c.codeType}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{c.code}</td>
                    <td className="py-2 pr-3">{c.label ?? "—"}</td>
                    <td className="py-2 pr-3 text-xs">{c.notes ?? "—"}</td>
                    {canDelete && (
                      <td className="py-2 text-right">
                        <RowActions
                          maxInline={1}
                          actions={[
                            {
                              label: "ລຶບ",
                              variant: "destructive",
                              onClick: async () => {
                                const ok = await confirm({
                                  title: "ລຶບ code",
                                  description: "ຕ້ອງການລຶບ code ນີ້ບໍ?",
                                  actionText: "ລຶບ",
                                  ActionProps: { variant: "destructive" },
                                });
                                if (!ok) return;
                                try {
                                  await deleteCode.mutateAsync(c.id);
                                } catch {
                                  /* */
                                }
                              },
                            },
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                ))}
                {modelCodes.length === 0 && (
                  <tr>
                    <td className="py-3 text-muted-foreground" colSpan={5}>
                      ຍັງບໍ່ມີ spec/option code
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        open={codeOpen}
        onOpenChange={(o) => {
          setCodeOpen(o);
          if (!o) resetCodeForm();
        }}
        title="ເພີ່ມ spec / option code"
        size="md"
      >
        <div className="grid gap-2">
          <SimpleSelect
            value={nfCodeType}
            onValueChange={(v) => setNfCodeType(v as "spec" | "option")}
            options={[
              { value: "spec", label: "Spec" },
              { value: "option", label: "Option" },
            ]}
          />
          <Input
            placeholder="Code"
            value={nfCode}
            onChange={(e) => setNfCode(e.target.value)}
          />
          <Input
            placeholder="Label"
            value={nfCodeLabel}
            onChange={(e) => setNfCodeLabel(e.target.value)}
          />
          <Input
            placeholder="ໝາຍເຫດ"
            value={nfCodeNotes}
            onChange={(e) => setNfCodeNotes(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCodeOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={createCode.isPending}
              onClick={async () => {
                if (!nfCode.trim()) {
                  toast.error("ຕ້ອງມີ code");
                  return;
                }
                try {
                  await createCode.mutateAsync({
                    codeType: nfCodeType,
                    code: nfCode.trim(),
                    label: nfCodeLabel.trim() ? nfCodeLabel.trim() : null,
                    notes: nfCodeNotes.trim() ? nfCodeNotes.trim() : null,
                  });
                  setCodeOpen(false);
                  resetCodeForm();
                } catch {
                  /* */
                }
              }}
            >
              ບັນທຶກ
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
